import React from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Eye, UserPlus, Megaphone } from "lucide-react";
import { fileUrl, formatFollowers } from "@/lib/api";
import { CreatorBadges, GiftedTag, ProBadge } from "@/components/Badges";
import { Stars } from "@/components/Rating";
import { PLATFORM_META } from "@/components/SocialIcons";
import { MatchBadge } from "@/components/MatchBadge";
import { Button } from "@/components/ui/button";

export function CreatorCard({ creator, index = 0, inviteMode = false, invited = false, onInvite, note, footer }) {
  const navigate = useNavigate();
  const socials = creator.socials || {};
  const activePlatforms = Object.keys(socials).filter(
    (p) => PLATFORM_META[p] && socials[p] && (socials[p].username || socials[p].followers)
  );
  const topViews = Math.max(0, ...activePlatforms.map((p) => Number(socials[p].avg_views) || 0));
  const services = creator.services || [];
  const priced = services.filter((s) => s.price_from != null && s.price_from !== "");
  const cheapest = priced.length
    ? priced.reduce((a, b) => (Number(a.price_from) < Number(b.price_from) ? a : b))
    : null;

  return (
    <div
      data-testid={`creator-card-${creator.user_id}`}
      onClick={() => navigate(`/creators/${creator.user_id}`)}
      style={{ animationDelay: `${index * 60}ms` }}
      className="animate-fade-up group cursor-pointer overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative h-44 w-full overflow-hidden bg-zinc-100">
        {creator.profile_photo ? (
          <img
            src={fileUrl(creator.profile_photo)}
            alt={creator.display_name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-100 to-indigo-100 text-3xl font-display font-bold text-emerald-700">
            {creator.display_name?.[0] || "?"}
          </div>
        )}
        {creator.gifted_ok && (
          <div className="absolute right-3 top-3">
            <GiftedTag />
          </div>
        )}
        {creator.promoted && (
          <div data-testid="creator-promoted-tag" className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm" title="This creator has a Pro plan and receives a small visibility boost in relevant searches.">
            <Megaphone className="h-3 w-3" /> Promoted
          </div>
        )}
      </div>

      <div className="p-4">
        {creator.match && <div className="mb-2"><MatchBadge match={creator.match} testid={`creator-match-${creator.user_id}`} /></div>}
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-bold tracking-tight text-zinc-900">
            {creator.display_name}
          </h3>
        </div>
        <div className="mb-2 flex items-center gap-1 text-sm text-zinc-500">
          <MapPin className="h-3.5 w-3.5" />
          <span>{creator.location || "UK"}</span>
          {creator.distance_km != null && (
            <span data-testid="creator-distance" className="ml-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">{creator.distance_km} km</span>
          )}
        </div>

        <div className="mb-3 flex flex-wrap gap-1.5">
          {(creator.niches || []).slice(0, 3).map((n) => (
            <span key={n} className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600">
              {n}
            </span>
          ))}
        </div>

        {note && (
          <p data-testid="creator-card-note" className="mb-3 text-xs font-medium text-emerald-700">{note}</p>
        )}

        <div className="mb-3 space-y-1.5">
          {activePlatforms.slice(0, 2).map((p) => {
            const { Icon, color, label } = PLATFORM_META[p];
            return (
              <div key={p} className="flex items-center gap-2 text-sm">
                <Icon className={`h-4 w-4 ${color}`} />
                <span className="text-zinc-600">{label}</span>
                <span className="ml-auto font-mono font-semibold text-zinc-900">
                  {formatFollowers(socials[p].followers)}
                </span>
              </div>
            );
          })}
          {topViews > 0 && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Eye className="h-4 w-4" />
              <span>Typical views</span>
              <span className="ml-auto font-mono font-semibold text-zinc-700">
                {formatFollowers(topViews)}
              </span>
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-3">
          <div>
            {cheapest ? (
              <span className="text-sm text-zinc-500">
                {cheapest.type.replace("Instagram ", "")} from{" "}
                <span className="font-display font-bold text-emerald-600">£{cheapest.price_from}</span>
              </span>
            ) : (
              <span className="text-sm text-zinc-400">Contact for pricing</span>
            )}
          </div>
          {creator.rating_count > 0 && (
            <div className="flex items-center gap-1" data-testid="creator-rating">
              <Stars value={creator.rating_avg} size={13} />
              <span className="text-xs font-semibold text-zinc-500">{creator.rating_avg}</span>
            </div>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <CreatorBadges badges={creator.badges} />
          {creator.is_pro && <ProBadge />}
        </div>

        {inviteMode && (
          <Button
            data-testid={`invite-creator-${creator.user_id}`}
            disabled={invited}
            onClick={(e) => { e.stopPropagation(); onInvite?.(creator); }}
            className={`mt-3 h-10 w-full rounded-full ${invited ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-emerald-600 hover:bg-emerald-700"}`}
          >
            <UserPlus className="mr-2 h-4 w-4" /> {invited ? "Invited" : "Invite"}
          </Button>
        )}

        {footer && (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
